import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../config/firebaseConfig";
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Laptop,
  Star,
  GraduationCap,
  Bot,
  Timer,
  Sparkles
} from "lucide-react";

const HeroSlider = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const totalSlides = 4;
  const duration = 6000; /* 6 seconds per slide */

  const slidesData = [
    {
      className: "s1",
      num: "01 / 04",
      icon: "🚀",
      tag: "AI & Robotics Lab · Online + Offline",
      title1: "Future Skills ",
      title2: "Start Here.",
      subtitle: "AI, Robotics & Coding for Kids.",
      sub: "Hands-on courses at our Panipat center or live online from anywhere in India. For ages 6–18.",
      chips: ["Ages 6–18", "NEP 2020 Aligned", "Real Projects", "Live + Recorded", "Expert Mentors", "Weekend Batches"],
      btnDark: { label: "Explore Courses", link: "/ai-robotics-coding-classes-for-kids" },
      btnGhost: {
        label: "Book Free Demo",
        link: auth?.currentUser ? "/dashboard?type=book-robotics-lab-demo" : "/register?type=book-robotics-lab-demo"
      }
    },
    {
      className: "s2",
      num: "02 / 04",
      icon: "🏆",
      tag: "Olympiad Practice App",
      title1: "Practice. ",
      title2: "Compete.",
      subtitle: "Ace Every Olympiad.",
      sub: "Real exam-style mock tests for SOF, SilverZone, Crest and more. Topic-wise practice for Class 1–10.",
      chips: ["Class 1–10", "SOF · SilverZone · Crest", "Free App", "50,000+ Students"],
      img: "/img/banner-1.png",
      imgAlt: "Olympiad Practice App",
      btnDark: { label: "Download Free App", link: "/olympiad-preparation-apps" },
      btnGhost: { label: "Know More", link: "/about" }
    },
    {
      className: "s3",
      num: "03 / 04",
      icon: "⚡",
      tag: "AI Assessment Platform",
      title1: "Smarter Exams. ",
      title2: "Zero Compromise.",
      subtitle: "Full AI Proctoring.",
      sub: "AI-proctored online exams with facial recognition, automated grading and instant results. Powered by Talentigo AI.",
      chips: ["AI Proctoring", "Instant Results", "Admission Tests", "Coaching Mock Tests"],
      btnDark: { label: "Book Free Demo", link: "https://wa.me/919560734660?text=Hello%20Practido,%20I%20am%20interested%20in%20booking%20a%20free%20demo%20class%20for%20my%20child." },
      btnGhost: { label: "See Features", link: "/contactus" }
    },
    {
      className: "s4",
      num: "04 / 04",
      icon: "🏫",
      tag: "For Schools",
      title1: "AI Lab in ",
      title2: "Your School.",
      subtitle: "Complete Setup. Zero Hassle.",
      sub: "Curriculum, teacher training, robotics kits, assessments and ongoing support — all included. You provide the space.",
      chips: ["Classes 1–12", "CBSE & ICSE", "NEP 2020", "Teacher Training"],
      btnDark: { label: "Book Free Consultation", link: "/contactus" },
      btnGhost: { label: "Know More", link: "/practido-for-schools" }
    }
  ];

  useEffect(() => {
    const intervalTime = 50; /* Update progress every 50ms for smooth rendering */
    const increment = (intervalTime / duration) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setCurrentSlide((slide) => (slide + 1) % totalSlides);
          return 0;
        }
        return prev + increment;
      });
    }, intervalTime);

    return () => {
      clearInterval(timer);
    };
  }, [currentSlide]);

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setProgress(0);
  };

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setProgress(0);
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  const handleNavigate = (link) => {
    if (!link) return;
    if (link.startsWith("http") || link.startsWith("https") || link.startsWith("wa.me")) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else if (link.includes("#")) {
      const [path, hash] = link.split("#");
      navigate(path);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    } else {
      navigate(link);
    }
  };

  // Render Coded Graphic for Slide 1
  const renderSlide1Graphic = () => {
    return (
      <div className="s1-graphic-wrapper">
        <div className="s1-cards-row">
          {/* At Our Center Card */}
          <div className="s1-card center-card">
            <div className="s1-card-header green-header">
              <MapPin size={14} className="icon" />
              <span>AT OUR CENTER</span>
            </div>
            <div className="s1-card-body">
              <div className="s1-card-top">
                <div className="avatar-placeholder green-avatar">
                  <GraduationCap size={18} />
                </div>
                <div className="s1-card-meta">
                  <h5>Panipat, Haryana</h5>
                  <p>Mon - Sat · 9 am - 6 pm</p>
                </div>
              </div>
              <ul className="s1-card-list">
                <li>AI & Robotics</li>
                <li>Coding & STEM</li>
                <li>Aerodynamics</li>
              </ul>
              <div className="s1-card-rating">
                <div className="stars">
                  <Star size={11} fill="#FBBF24" stroke="none" />
                  <Star size={11} fill="#FBBF24" stroke="none" />
                  <Star size={11} fill="#FBBF24" stroke="none" />
                  <Star size={11} fill="#FBBF24" stroke="none" />
                  <Star size={11} fill="#FBBF24" stroke="none" />
                </div>
                <span>4.9 · 500+ students</span>
              </div>
            </div>
          </div>

          {/* Plus separator */}
          <div className="plus-separator-badge">
            <span>+</span>
          </div>

          {/* Online Classes Card */}
          <div className="s1-card online-card">
            <div className="s1-card-header purple-header">
              <Laptop size={14} className="icon" />
              <span>ONLINE CLASSES</span>
            </div>
            <div className="s1-card-body">
              <div className="s1-card-top">
                <div className="avatar-placeholder purple-avatar">
                  <Laptop size={18} />
                </div>
                <div className="s1-card-meta">
                  <h5>From Anywhere</h5>
                  <p>Live + Self-paced</p>
                </div>
              </div>
              <ul className="s1-card-list">
                <li>Live Classes</li>
                <li>Small Batches</li>
                <li>Certifications</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="s1-stats-grid">
          <div className="s1-stat-box">
            <h4>6-18</h4>
            <p>Ages</p>
          </div>
          <div className="s1-stat-box">
            <h4>40+</h4>
            <p>Courses</p>
          </div>
          <div className="s1-stat-box">
            <h4>NEP</h4>
            <p>2020</p>
          </div>
          <div className="s1-stat-box">
            <h4>10K+</h4>
            <p>Alumni</p>
          </div>
        </div>

        {/* Technology tags */}
        <div className="s1-pills-row">
          <span className="s1-pill">AI</span>
          <span className="s1-pill">Coding</span>
          <span className="s1-pill">Design</span>
          <span className="s1-pill">Video</span>
          <span className="s1-pill">3D Print</span>
        </div>
      </div>
    );
  };

  // Render Graphic for Slide 2
  const renderSlide2Graphic = () => {
    return (
      <div className="s2-graphic-wrapper">
        <img className="slide-2-img" src="/img/banner-1.png" alt="Olympiad Practice App Mockup" />
      </div>
    );
  };

  // Render Coded Graphic for Slide 3
  const renderSlide3Graphic = () => {
    return (
      <div className="s3-graphic-wrapper">
        {/* Laptop Proctoring Mockup */}
        <div className="laptop-mockup-container">
          {/* Floating feature popups (moved outside screen, balanced left and right bezel overlays) */}
          <div className="floating-card secure-exam">
            <div className="card-icon">🔒</div>
            <div>
              <strong>Secure Exam</strong>
              <p>End-to-end encrypted</p>
            </div>
          </div>

          <div className="floating-card instant-result">
            <div className="card-icon">⚡</div>
            <div>
              <strong>Instant Result</strong>
              <p>Auto-graded by AI</p>
            </div>
          </div>

          <div className="laptop-screen-frame">
            <div className="laptop-screen-inner">
              {/* Screen Top Nav */}
              <div className="screen-header">
                <div className="live-rec">
                  <span className="live-dot"></span>
                  <span>LIVE REC</span>
                </div>
                <div className="ai-proctor-badge">
                  <Bot size={13} className="icon" />
                  <span>AI PROCTORING</span>
                </div>
              </div>

              {/* Screen Content Grid */}
              <div className="screen-main-content">
                {/* Students Sidebar */}
                <div className="screen-sidebar">
                  <h6>Students</h6>
                  <div className="student-status-dots">
                    <span className="status-dot green"></span>
                    <span className="status-dot red"></span>
                    <span className="status-dot green"></span>
                    <span className="status-dot red"></span>
                  </div>
                </div>

                {/* Center Face View */}
                <div className="screen-face-view">
                  <div className="webcam-box">
                    <div className="bounding-box"></div>
                    <div className="detected-face">👦</div>
                  </div>
                  <div className="verified-badge">
                    <span className="icon">✓</span> VERIFIED
                  </div>
                </div>

                {/* Timer */}
                <div className="screen-timer">
                  <Timer size={12} className="timer-icon" />
                  <span>58:42 left</span>
                </div>
              </div>
            </div>
          </div>
          <div className="laptop-base-stand"></div>
        </div>

        {/* Stats Grid */}
        <div className="s3-stats-grid">
          <div className="s3-stat-box">
            <h4>AI</h4>
            <p>Proctored</p>
          </div>
          <div className="s3-stat-box">
            <h4>99%</h4>
            <p>Accuracy</p>
          </div>
          <div className="s3-stat-box">
            <h4>0</h4>
            <p>Malpractice</p>
          </div>
          <div className="s3-stat-box">
            <h4>∞</h4>
            <p>Scalable</p>
          </div>
        </div>
      </div>
    );
  };

  // Render Coded Graphic for Slide 4
  const renderSlide4Graphic = () => {
    return (
      <div className="s4-graphic-wrapper">
        <div className="school-cards-layout">
          {/* School Building Center SVG Graphic (restored to code, enlarged & heightened) */}
          <div className="school-building-center">
            <svg viewBox="0 0 200 200" className="school-building-svg">
              <polygon points="100,10 15,65 185,65" fill="#7C3AED" />
              <line x1="100" y1="10" x2="100" y2="0" stroke="#4B5563" strokeWidth="2" />
              <polygon points="100,0 115,5 100,10" fill="#EF4444" />
              <rect x="25" y="65" width="150" height="125" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="2" rx="6" />

              <rect x="40" y="80" width="22" height="22" fill="#E8EDF9" rx="3" />
              <rect x="73" y="80" width="22" height="22" fill="#E8EDF9" rx="3" />
              <rect x="105" y="80" width="22" height="22" fill="#E8EDF9" rx="3" />
              <rect x="138" y="80" width="22" height="22" fill="#E8EDF9" rx="3" />

              <rect x="40" y="112" width="22" height="22" fill="#E8EDF9" rx="3" />
              <rect x="138" y="112" width="22" height="22" fill="#E8EDF9" rx="3" />

              <rect x="82" y="145" width="36" height="45" fill="#7C3AED" rx="4" />
              <circle cx="108" cy="168" r="2.5" fill="#FFFFFF" />
            </svg>

            {/* Title badge moved inside the building walls (above the door) */}
            <div className="school-floating-badge">PRACTIDO AI LAB</div>

            <div className="robot-mascot-badge">
              <svg viewBox="0 0 100 100" className="robot-mascot-svg">
                <rect x="20" y="30" width="60" height="50" rx="10" fill="#3B82F6" />
                <line x1="50" y1="30" x2="50" y2="15" stroke="#3B82F6" strokeWidth="4" />
                <circle cx="50" cy="15" r="6" fill="#FBBF24" />
                <circle cx="40" cy="50" r="8" fill="#FFFFFF" />
                <circle cx="40" cy="50" r="4" fill="#1E3A8A" />
                <circle cx="60" cy="50" r="8" fill="#FFFFFF" />
                <circle cx="60" cy="50" r="4" fill="#1E3A8A" />
                <path d="M 45,68 Q 50,73 55,68" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" />
                <rect x="12" y="45" width="8" height="20" rx="3" fill="#1D4ED8" />
                <rect x="80" y="45" width="8" height="20" rx="3" fill="#1D4ED8" />
              </svg>
            </div>

            <div className="decor-star star-l">⭐</div>
            <div className="decor-star star-r">⭐</div>
          </div>

          {/* Floating cards */}
          <div className="f-card c-top-left">
            <span>Curriculum</span>
            <strong>Included</strong>
          </div>
          <div className="f-card c-top-right">
            <span>Robotics Kit</span>
            <strong>Provided</strong>
          </div>
          <div className="f-card c-bottom-left">
            <span>Teacher</span>
            <strong>Training</strong>
          </div>
          <div className="f-card c-bottom-right">
            <span>Assessments</span>
            <strong>All included</strong>
          </div>
        </div>

        {/* School tags underneath */}
        <div className="s4-pills-row">
          <span className="s4-pill">CBSE & ICSE</span>
          <span className="s4-pill">NEP 2020</span>
          <span className="s4-pill">Classes 1–10</span>
          <span className="s4-pill">Zero CAPEX</span>
        </div>
      </div>
    );
  };

  return (
    <div className={`hero-slider-container theme-${currentSlide === 0 ? "green" :
      currentSlide === 1 ? "blue" :
        currentSlide === 2 ? "orange" :
          "purple"
      }`}>
      <div className="slider-wrap" id="slider">
        <div
          className="slides"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slidesData.map((slide, index) => (
            <div key={index} className={`slide ${slide.className} ${currentSlide === index ? "active-slide" : ""}`}>
              <div className="container">
                <div className="row align-items-center">
                  <div className="col-md-12 col-lg-6">
                    <div className="slide-header-meta">
                      <div className="badge hero-badge slide-tag">
                        {slide.icon && <span className="tag-emoji">{slide.icon}</span>}
                        <span>{slide.tag}</span>
                      </div>
                      <span className="slide-num">{slide.num}</span>
                    </div>
                    <h1 className="slide-h1">
                      <span className="title-text">{slide.title1}</span>{" "}
                      <span className="title-highlight">{slide.title2}</span>
                    </h1>
                    {slide.subtitle && (
                      <div className="hero-supporting-line slide-supporting-line">
                        {slide.subtitle}
                      </div>
                    )}
                    <p className="slide-sub">{slide.sub}</p>
                    <div className="slide-chips">
                      {slide.chips.map((chip, chipIdx) => (
                        <span key={chipIdx} className="chip">
                          {chip}
                        </span>
                      ))}
                    </div>
                    <div className="slide-btns hero-buttons">
                      {slide.btnDark && (
                        <button
                          type="button"
                          className="btn primary-btn"
                          onClick={() => handleNavigate(slide.btnDark.link)}
                        >
                          <Sparkles size={16} strokeWidth={2.5} />
                          <span>{slide.btnDark.label}</span>
                        </button>
                      )}
                      {slide.btnGhost && (
                        <button
                          type="button"
                          className="btn blue-btn"
                          onClick={() => handleNavigate(slide.btnGhost.link)}
                        >
                          <span>{slide.btnGhost.label}</span>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12 col-lg-6">
                    <div className="slide-img-container">
                      {index === 0 && renderSlide1Graphic()}
                      {index === 1 && renderSlide2Graphic()}
                      {index === 2 && renderSlide3Graphic()}
                      {index === 3 && renderSlide4Graphic()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Centered controls below the slider wrapper with custom progress bars inside dots */}
      <div className="controls-outer-container">
        <div className="container">
          <div className="slider-controls-group">
            <button className="control-arrow control-arrow-l" onClick={handlePrev} aria-label="Previous slide">
              <ChevronLeft size={16} />
            </button>
            <div className="progress-dots">
              {Array.from({ length: totalSlides }).map((_, i) => {
                const isActive = currentSlide === i;
                return (
                  <button
                    key={i}
                    className={`progress-dot ${isActive ? "active" : ""}`}
                    onClick={() => handleDotClick(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  >
                    {isActive && (
                      <span
                        className="progress-fill-bar"
                        style={{ width: `${progress}%` }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
            <button className="control-arrow control-arrow-r" onClick={handleNext} aria-label="Next slide">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSlider;
